/**
 *
 * Implements BOOK class, CanonicalBook
 *
 * as well as functions to load books from Google/ISBN and NYT
 *
 */

import { ulid } from 'ulid'
import { z } from 'zod'
import type { SharedJournal } from './sharedJournal'
import type Journal from './journal'

export default Book

export const AmazonBookSchema = z.object({
	ASIN: z.string(),
	image_s: z.string().optional(),
	image_m: z.string().optional(),
	image_l: z.string().optional(),
	url: z.string().optional(),
	title: z.string().optional(),
	author: z.string().optional(),
	publishedDate: z.string().optional().nullable(),
	pages: z.number().optional()
})

export type AmazonBook = z.infer<typeof AmazonBookSchema>
export const createAmazonBook = (initialValue: AmazonBook | Object = {}) =>
	AmazonBookSchema.parse(initialValue)

// TODO - single create
export const BookSchema = z.object({
	uuid: z.string().optional().default(''),
	title: z.string().optional().default(''),
	book_image: z.string().optional().default(''),
	book_image_s: z.string().optional().default(''),
	book_image_m: z.string().optional().default(''),
	book_image_l: z.string().optional().default(''),
	book_image_xl: z.string().optional().default(''),
	author: z.string().optional().default(''),
	description: z.string().optional().default(''),
	isbn_10: z.string().optional().default(''),
	isbn_13: z.string().optional().default(''),
	googleBookId: z.string().optional().default(''),
	amazonBook: AmazonBookSchema.optional(),
	pages: z.number().optional().default(0).or(z.string().default('0')),
	publishedDate: z.string().optional().default(''),
	buyLink: z.string().optional().default(''),
	categories: z.array(z.string()).optional().default([]),
	averageRating: z.number().optional().default(0),
	ratingsCount: z.number().optional().default(0),
	mainCategory: z.string().optional().default(''),
	previewLink: z.string().optional().default(''),
	cover_image: z.string().optional().default(''),
	cover_image_thumbnail: z.string().optional().default(''),
	cover_type: z.string().optional().default('default'),
	creator: z.string().optional().default(''),
	tags: z.string().optional().default(''),
	likes: z.number().optional().default(0),
	ratingAvg: z.number().optional().default(0),
	ratingCount: z.number().optional().default(0),
	altRatingAvg: z.number().optional().default(0),
	altRatingCount: z.number().optional().default(0),
	tropes: z.array(z.string()).optional().default([]),
	series: z.string().optional().default('')
})

export type Book = z.infer<typeof BookSchema>
export const createBook = (initialValue: Book | Object = {}): Book => BookSchema.parse(initialValue)

export const createCustomBook = (userID: string): Book =>
	BookSchema.parse({
		creator: userID,
		uuid: `book:custom:${ulid().slice(-12)}`
	})

export enum SortMethod {
	NONE = 0,
	DATE,
	RATING,
	BOOK_TITLE,
	AUTHOR,
	PAGE_COUNT,
	READ_DATE
}

export const SortMethods = [
	{ name: 'None', method: SortMethod.NONE },
	{ name: 'Date', method: SortMethod.DATE },
	{ name: 'Rating', method: SortMethod.RATING },
	{ name: 'Book Title', method: SortMethod.BOOK_TITLE },
	{ name: 'Author', method: SortMethod.AUTHOR },
	{ name: 'Page Count', method: SortMethod.PAGE_COUNT },
]

/**
 *
 * @param method which sortMethod to use
 * @returns sort comparator function
 */
export const getSortComparator = (method: SortMethod) => {
	return (a: Book, b: Book) => {
		const dateA = new Date(a.publishedDate ?? '')
		const dateB = new Date(b.publishedDate ?? '')

		switch (method) {
			case SortMethod.DATE:
				return dateB < dateA ? 1 : -1
			case SortMethod.BOOK_TITLE:
				return a.title.localeCompare(b.title)
			case SortMethod.RATING:
				return b.averageRating - a.averageRating
			case SortMethod.AUTHOR:
				return a.author.localeCompare(b.author)
			case SortMethod.PAGE_COUNT:
				return Number(a.pages) - Number(b.pages)
			default:
				return 1
		}
	}
}

export const getBookLikeCount = (
	likes: Array<{ bookID: string; likes: number }>,
	bookID: string
): number => {
	const found = likes.find((el) => el.bookID === bookID)

	if (found) return found.likes
	else return 0
}

export const getBookMethod = (books: Array<Book>) => {
	return (bookID: string) => {
		const book = books.find((el) => el.uuid === bookID)
		if (!book) return undefined
		else return book
	}
}

/**
 *
 * @param book book to search
 * @returns highes res image available
 */
export const getHighestResImage = (book: Book): string => {
	return getBookImage(book, 'xl')
}
/**
 *
 * @param book book to evalaute
 * @returns whether this book has an image
 */
export const hasBookImage = (book: Book): boolean => {
	if (!book || !book.cover_type) {
		return false
	} 
	
	if (book.cover_type != undefined && book.cover_type != null && book.cover_type === 'image') {
		return book.cover_image || book.cover_image_thumbnail ? true : false
	} else {
		if (book.book_image) return book.book_image.length > 0
	}
	return false
}
/**
 *
 * @param book book to search
 * @param size which size image
 * @returns image
 */
export const getBookImage = (book: Book, size: string): string => {
	if (book.cover_type === 'image') {
		if (size === 'sm' && book.cover_image_thumbnail) return book.cover_image_thumbnail
		else if (book.cover_image) return book.cover_image
	}
	let img: string = ''

	if (size === 'xl' || size === 'lg') {
		if (book.amazonBook && book.amazonBook.image_l) img = book.amazonBook.image_l
		else {
			img = book.book_image_xl || book.book_image_l || ''
		}
	}
	if (size === 'md' && img === '') {
		if (book.amazonBook && book.amazonBook.image_m) img = book.amazonBook.image_m
		else img = book.book_image_m || ''
	}
	if (size === 'sm' && img === '') {
		if (book.amazonBook && book.amazonBook.image_s) img = book.amazonBook.image_s
		else img = book.book_image_m || ''
	}
	if (img === '') {
		img = book.book_image || ''
	}

	if (img === '') {
		// fallback
		if (book.book_image) img = book.book_image
		else if (book.book_image_s) img = book.book_image_s
		else if (book.book_image_m) img = book.book_image_m
		else if (book.book_image_l) img = book.book_image_l
		else if (book.book_image_xl) img = book.book_image_xl

		if (img === '') console.error('Warning: found no image for:', book.uuid)
	}
	return img
}

export type BookReview = {
	notes: string
	rating: number
	altRating: number
	tags: string[]
	spoilerAlert: boolean
	creator: string
	creatorName: string
	creatorImage: string | null
	creatorURL: string | null
	creatorUsername: string
	journal: Journal
}
/**
 * ISBNDB Type
 */
/**
 *
 * @param _book ISBNDB Book object
 */
export const createBookFromISBN = (_book: ISBNDB_Book): Book => {
	let book = BookSchema.parse({
		title: _book.title,
		book_image: _book.image,
		author: _book.authors ? _book.authors[0] : '',
		isbn_10: _book.isbn || '',
		isbn_13: _book.isbn13 || '',
		pages: _book.pages,
		publishedDate: _book.date_published
	})

	if (_book.synopsys) book.description = _book.synopsys
	if (_book.synopsis) book.description = _book.synopsis

	return book
}

export interface ISBNDB_Book {
	publisher: string
	synopsys: string
	synopsis: string
	language: string
	image: string
	title_long: string
	edition: number
	pages: number
	date_published: string
	subjects: string[]
	authors: string[]
	title: string
	isbn13: string
	msrp: string
	binding: string
	isbn: string
}
